"use client";

import { memo, useMemo } from "react";
import { FileText, BarChart3, Link2 } from "lucide-react";

interface DataFrameInfo {
  data: Array<Record<string, any>>;
  schema: {
    columns: string[];
    types: Record<string, string>;
    row_count: number;
  };
  sources: Array<{ name: string; url: string }>;
  reasoning: string;
}

interface FundAgentSlideProps {
  query: string;
  dataframes: Record<string, DataFrameInfo>;
}

// Determine source type from URL
function getSourceTypeFromUrl(url: string): "sec" | "fmp" | "other" {
  const urlLower = url.toLowerCase();
  if (urlLower.includes("sec.gov")) return "sec";
  if (urlLower.includes("financialmodelingprep")) return "fmp";
  return "other";
}

// Get source icon based on type
function SourceIcon({ url, className }: { url: string; className?: string }) {
  const type = getSourceTypeFromUrl(url);
  if (type === "sec") return <FileText className={className + " text-amber-500"} />;
  if (type === "fmp") return <BarChart3 className={className + " text-emerald-500"} />;
  return <Link2 className={className + " text-muted-foreground/60"} />;
}

// Extract sources from a row (handles string or array)
function getRowSources(row: Record<string, any>): string[] {
  const src = row.source;
  if (!src) return [];
  if (Array.isArray(src)) return src.filter(Boolean);
  if (typeof src === "string" && src.trim()) return [src];
  return [];
}

// Open all sources for a row
function openRowSources(sources: string[]) {
  sources.forEach((url) => window.open(url, "_blank", "noopener,noreferrer"));
}

function FundAgentSlideComponent({ query, dataframes }: FundAgentSlideProps) {
  const dfEntries = Object.entries(dataframes || {});

  if (dfEntries.length === 0) {
    return (
      <div className="text-sm text-muted-foreground/60 py-3 text-center">
        No data returned.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {dfEntries.map(([name, df]) => (
        <div key={name} className="rounded-lg overflow-hidden bg-background">
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-mono font-semibold text-foreground">
                {name}
              </span>
              <span className="text-xs text-muted-foreground/60">
                {df.schema.row_count} × {df.schema.columns.length}
              </span>
            </div>
            
            {/* Source link - top right */}
            {df.sources && df.sources.length > 0 && (
              <a
                href={df.sources[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <SourceIcon url={df.sources[0].url} className="w-3.5 h-3.5" />
                <span className="max-w-[150px] truncate">{df.sources[0].name}</span>
              </a>
            )}
          </div>

          {/* Reasoning - always visible */}
          {df.reasoning && (
            <div className="px-3 pb-2">
              <p className="text-xs text-muted-foreground leading-relaxed">
                {df.reasoning}
              </p>
            </div>
          )}

          {/* Table */}
          <DataTable df={df} />
        </div>
      ))}
    </div>
  );
}

function formatValue(value: any, type?: string): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") {
    if (Number.isInteger(value) && Math.abs(value) >= 1000) {
      return value.toLocaleString();
    }
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return String(value);
}

// DataTable component with source column rendered as icons
function DataTable({ df }: { df: DataFrameInfo }) {
  // Filter out 'source' from visible columns
  const visibleColumns = useMemo(
    () => df.schema.columns.filter((col) => col !== "source"),
    [df.schema.columns]
  );

  // Check if source column exists
  const hasSourceColumn = df.schema.columns.includes("source");

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted/15">
            {hasSourceColumn && (
              <th className="px-2 py-1.5 text-left font-semibold text-foreground/70 whitespace-nowrap uppercase text-xs tracking-wide w-8">
                
              </th>
            )}
            {visibleColumns.map((col) => (
              <th
                key={col}
                className="px-3 py-1.5 text-left font-semibold text-foreground/70 whitespace-nowrap uppercase text-xs tracking-wide"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {df.data.map((row, idx) => {
            const sources = getRowSources(row);
            return (
              <tr
                key={idx}
                className={`hover:bg-muted/10 transition-colors ${
                  sources.length > 0 ? "cursor-pointer" : ""
                }`}
                onClick={() => sources.length > 0 && openRowSources(sources)}
              >
                {hasSourceColumn && (
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="flex items-center gap-0.5">
                      {sources.map((url, i) => (
                        <SourceIcon key={i} url={url} className="w-3.5 h-3.5" />
                      ))}
                    </div>
                  </td>
                )}
                {visibleColumns.map((col) => (
                  <td
                    key={col}
                    className="px-3 py-1.5 text-foreground/90 whitespace-nowrap tabular-nums"
                  >
                    {formatValue(row[col], df.schema.types[col])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export const FundAgentSlide = memo(FundAgentSlideComponent);
